import { useEffect, useState } from 'react';
import { ExternalLink, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useSupplier,
  useCreateSupplier,
  useUpdateSupplier,
  useDeleteSupplier,
} from '../../hooks/useWmsSuppliers';
import type { SupplierKind, SupplierStatus, UpdateSupplierInput } from '../../types';
import { SupplierDriveFiles } from './SupplierDriveFiles';

const KIND_OPTIONS: { value: SupplierKind; label: string }[] = [
  { value: 'hardware', label: 'Hardware' },
  { value: 'software', label: 'Software' },
  { value: 'service', label: 'Service' },
  { value: 'admin', label: 'Admin' },
  { value: 'carrier', label: 'Carrier' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS: { value: SupplierStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

interface SupplierDetailPanelProps {
  supplierId: string | null;
  mode: 'view' | 'create';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
  defaultKind?: SupplierKind;
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

export function SupplierDetailPanel({
  supplierId,
  mode,
  open,
  onOpenChange,
  onCreated,
  defaultKind = 'other',
}: SupplierDetailPanelProps) {
  const isCreate = mode === 'create';
  const supplierQuery = useSupplier(isCreate ? undefined : supplierId ?? undefined);
  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();
  const deleteMutation = useDeleteSupplier();

  const supplier = supplierQuery.data;

  const [name, setName] = useState('');
  const [kind, setKind] = useState<SupplierKind>(defaultKind);
  const [status, setStatus] = useState<SupplierStatus>('active');
  const [description, setDescription] = useState('');
  const [hubspot, setHubspot] = useState('');
  const [drive, setDrive] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Sync form state when a different supplier is loaded (NOT every refetch).
  useEffect(() => {
    if (isCreate) {
      setName('');
      setKind(defaultKind);
      setStatus('active');
      setDescription('');
      setHubspot('');
      setDrive('');
      setWebsite('');
      setEmail('');
      setPhone('');
      setTags([]);
      return;
    }
    if (supplier) {
      setName(supplier.name);
      setKind(supplier.supplier_kind ?? 'other');
      setStatus(supplier.status);
      setDescription(stripHtml(supplier.description ?? ''));
      setHubspot(supplier.hubspot_company_id ?? '');
      setDrive(supplier.gdrive_root_folder_id ?? '');
      setWebsite(supplier.website ?? '');
      setEmail(supplier.email ?? '');
      setPhone(supplier.phone ?? '');
      setTags(supplier.tags ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier?.id, isCreate]);

  const save = (data: UpdateSupplierInput) => {
    if (!supplier?.id || isCreate) return;
    updateMutation.mutate({ id: supplier.id, data });
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    const created = await createMutation.mutateAsync({
      name: name.trim(),
      supplier_kind: kind,
      status,
      description: description.trim() || undefined,
      hubspot_company_id: hubspot.trim() || undefined,
      gdrive_root_folder_id: drive.trim() || undefined,
      website: website.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      tags: tags.length > 0 ? tags : undefined,
    });
    onCreated?.(created.id);
  };

  const handleDelete = async () => {
    if (!supplier?.id) return;
    await deleteMutation.mutateAsync(supplier.id);
    setConfirmDelete(false);
    onOpenChange(false);
  };

  const driveUrl = drive.trim()
    ? `https://drive.google.com/drive/folders/${encodeURIComponent(drive.trim())}`
    : null;

  if (!open) return null;

  return (
    <div className="h-full flex flex-col border-l bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {!isCreate && supplier?.account_number && (
            <span className="text-xs text-muted-foreground tabular-nums shrink-0">
              {supplier.account_number}
            </span>
          )}
          <h2 className="text-sm font-semibold truncate">
            {isCreate ? 'New Supplier' : supplier?.name ?? 'Supplier'}
          </h2>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isCreate && supplier && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setConfirmDelete(true)}
              title="Delete supplier"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onOpenChange(false)}
            title="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <Section label="Name *">
          <Input
            className="text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => !isCreate && supplier && name !== supplier.name && name.trim() && save({ name: name.trim() })}
            placeholder="Supplier name"
            autoFocus={isCreate}
          />
        </Section>

        <div className="grid grid-cols-2 gap-3">
          <Section label="Kind">
            <Select
              value={kind}
              onValueChange={(v) => {
                if (!v) return;
                const next = v as SupplierKind;
                setKind(next);
                if (!isCreate) save({ supplier_kind: next });
              }}
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Kind" />
              </SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Section>
          <Section label="Status">
            <Select
              value={status}
              onValueChange={(v) => {
                if (!v) return;
                const next = v as SupplierStatus;
                setStatus(next);
                if (!isCreate) save({ status: next });
              }}
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Section>
        </div>

        <Section label="Google Drive Folder ID">
          <div className="flex items-center gap-2">
            <Input
              className="text-sm flex-1 font-mono"
              value={drive}
              onChange={(e) => setDrive(e.target.value)}
              onBlur={() => !isCreate && supplier && drive !== (supplier.gdrive_root_folder_id ?? '') && save({ gdrive_root_folder_id: drive.trim() || undefined })}
              placeholder="e.g. 1eJmOKqoaNg4gwDtH8rxBnlqMcSYpyLGh"
            />
            {driveUrl && (
              <Button asChild variant="outline" size="sm" className="shrink-0">
                <a href={driveUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Open
                </a>
              </Button>
            )}
          </div>
          {!isCreate && supplier?.id && (
            <div className="mt-2">
              <SupplierDriveFiles supplierId={supplier.id} />
            </div>
          )}
        </Section>

        <Section label="Website">
          <Input
            className="text-sm"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            onBlur={() => !isCreate && supplier && website !== (supplier.website ?? '') && save({ website: website.trim() || null })}
            placeholder="https://example.com"
          />
        </Section>

        <div className="grid grid-cols-2 gap-3">
          <Section label="Email">
            <Input
              type="email"
              className="text-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => !isCreate && supplier && email !== (supplier.email ?? '') && save({ email: email.trim() || null })}
              placeholder="contact@example.com"
            />
          </Section>
          <Section label="Phone">
            <Input
              className="text-sm"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => !isCreate && supplier && phone !== (supplier.phone ?? '') && save({ phone: phone.trim() || null })}
              placeholder="+1 555 123-4567"
            />
          </Section>
        </div>

        <Section label="HubSpot Company ID">
          <Input
            className="text-sm font-mono"
            value={hubspot}
            onChange={(e) => setHubspot(e.target.value)}
            onBlur={() => !isCreate && supplier && hubspot !== (supplier.hubspot_company_id ?? '') && save({ hubspot_company_id: hubspot.trim() || undefined })}
            placeholder="HubSpot ID"
          />
        </Section>

        <Section label="Description">
          <Textarea
            className="text-sm min-h-[120px]"
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => !isCreate && supplier && description !== stripHtml(supplier.description ?? '') && save({ description: description.trim() || undefined })}
            placeholder="Notes, contact details, payment terms..."
          />
        </Section>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t shrink-0">
        {isCreate ? (
          <Button
            className="w-full"
            disabled={!name.trim() || createMutation.isPending}
            onClick={handleCreate}
          >
            Create Supplier
          </Button>
        ) : supplier ? (
          <p className="text-xs text-muted-foreground">
            Created {new Date(supplier.created_at).toLocaleDateString()} · Updated {new Date(supplier.updated_at).toLocaleDateString()}
          </p>
        ) : null}
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete supplier?</AlertDialogTitle>
            <AlertDialogDescription>
              This will soft-delete <strong>{supplier?.name}</strong>. You can restore it later from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteMutation.isPending}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function stripHtml(s: string): string {
  return s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}
